import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Calendar, Edit, LogOut, Banknote, CheckCircle, FileText, Ban, UserX, AlertTriangle, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { GuestLink } from '../crm/EntityLinks';
import { formatCurrency } from './utils';
import { toNumber, safeFixed } from '../../utils/formatters';
import { PaymentList } from './PaymentComponents';
import { EventsList } from './EventComponents';
import PaxManager from '../PaxManager';
import PrecheckinLink from './PrecheckinLink';
import DocumentsSection from './DocumentsSection';
import CancelReservationDialog, { NoShowDialog } from './CancelReservationDialog';
import RefundsList from './RefundComponents';

export default function ReservationDetailDialog({
  open,
  onOpenChange,
  reservation,
  channels,
  statusLabels,
  paymentSummary,
  paymentData,
  paymentMethods,
  reservationEvents,
  eventTypeOptions,
  onEdit,
  onCheckout,
  onAddPayment,
  onDeletePayment,
  onAddEvent,
  onEditEvent,
  onDeleteEvent,
  onRefresh,
  canSeeFinancials = true
}) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showNoShowDialog, setShowNoShowDialog] = useState(false);
  
  if (!reservation) return null;

  const channel = channels.find(c => c.code === reservation.source);
  
  // Check if cancellation/no-show actions are allowed
  const canCancel = ['CONFIRMED', 'CHECKED_IN', 'PENDING'].includes(reservation.status);
  const canMarkNoShow = reservation.status === 'CONFIRMED';
  const isCancelled = reservation.status === 'CANCELLED';
  const isNoShow = reservation.status === 'NO_SHOW';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Réservation #{reservation?.platform_ref || reservation?.id}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto min-h-0">
          <Tabs defaultValue="info" className="w-full">
            <TabsList className={`grid w-full sticky top-0 bg-background z-10 ${
              canSeeFinancials && reservation.source === 'DIRECT' ? 'grid-cols-5' : 'grid-cols-4'
            }`}>
              <TabsTrigger value="info">Infos</TabsTrigger>
              <TabsTrigger value="documents" className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Documents
                {reservation.police_form_generated_at && (
                  <CheckCircle className="w-3 h-3 text-green-500" />
                )}
              </TabsTrigger>
              {canSeeFinancials && reservation.source === 'DIRECT' && (
                <TabsTrigger value="payments" className="flex items-center gap-1">
                  <Banknote className="w-4 h-4" />
                  Paiements
                  {paymentSummary.remaining > 0 && (
                    <Badge variant="destructive" className="ml-1 text-xs">
                      {formatCurrency(paymentSummary.remaining)}
                    </Badge>
                  )}
                  {paymentSummary.is_fully_paid && (
                    <CheckCircle className="w-4 h-4 text-green-500 ml-1" />
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="events">
                Services
                {reservationEvents?.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{reservationEvents.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="pax">
                PAX
                {reservation.pax_list?.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{reservation.pax_list.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>
          
            {/* Info Tab */}
            <TabsContent value="info" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1 col-span-2">
                  <p className="text-sm text-muted-foreground">Client</p>
                  <div className="flex items-center gap-2">
                    <GuestLink guest={reservation.guest} showIcon={true} variant="inline" />
                    {reservation.guest?.email && (
                      <span className="text-sm text-muted-foreground">• {reservation.guest.email}</span>
                    )}
                    {reservation.guest?.phone && (
                      <span className="text-sm text-muted-foreground">• {reservation.guest.phone}</span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Source</p>
                  <Badge 
                    style={{ 
                      backgroundColor: channel?.color ? `${channel.color}20` : undefined,
                      color: channel?.color,
                      borderColor: channel?.color
                    }}
                    variant="outline"
                  >
                    {channel?.label || reservation.source}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Réf. plateforme</p>
                  <p className="font-mono text-sm">{reservation.platform_ref || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Arrivée</p>
                  <p className="font-semibold">{format(new Date(reservation.check_in), 'dd/MM/yyyy')}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Départ</p>
                  <p className="font-semibold">{format(new Date(reservation.check_out), 'dd/MM/yyyy')}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Nuits</p>
                  <p className="font-semibold">{reservation.nights}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Occupants</p>
                  <p className="font-semibold">
                    {reservation.adults} adulte(s)
                    {reservation.children > 0 && `, ${reservation.children} enfant(s)`}
                  </p>
                </div>
                {canSeeFinancials && (
                  <>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Montant total</p>
                      <p className="font-semibold text-lg">
                        {formatCurrency(reservation.total_amount, reservation.currency)}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Commission</p>
                      <p className="font-semibold text-red-600">
                        -{formatCurrency(reservation.commission_amount || 0, reservation.currency)}
                        {toNumber(reservation.total_amount) > 0 && toNumber(reservation.commission_amount) > 0 && (
                          <span className="text-sm font-normal text-muted-foreground ml-1">
                            ({safeFixed((toNumber(reservation.commission_amount) / toNumber(reservation.total_amount)) * 100, 1)}%)
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Net à recevoir</p>
                      <p className="font-semibold text-green-600 text-lg">
                        {formatCurrency(
                          (reservation.total_amount || 0) - (reservation.commission_amount || 0), 
                          reservation.currency
                        )}
                      </p>
                    </div>
                  </>
                )}
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Statut</p>
                  <Badge 
                    variant={isCancelled ? 'destructive' : isNoShow ? 'outline' : 'default'}
                    className={isNoShow ? 'border-orange-500 text-orange-700 bg-orange-50' : ''}
                  >
                    {statusLabels[reservation.status]}
                  </Badge>
                </div>
              </div>
              
              {/* Cancellation/No-Show Info */}
              {(isCancelled || isNoShow) && (
                <div className={`p-4 rounded-lg border ${isCancelled ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                  <div className="flex items-start gap-3">
                    {isCancelled ? (
                      <Ban className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <UserX className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`font-semibold ${isCancelled ? 'text-red-800' : 'text-orange-800'}`}>
                        {isCancelled ? 'Réservation annulée' : 'Client non présenté (No-Show)'}
                      </p>
                      {reservation.cancelled_at && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Le {format(new Date(reservation.cancelled_at), "dd MMMM yyyy 'à' HH:mm", { locale: fr })}
                        </p>
                      )}
                      {reservation.no_show_at && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Le {format(new Date(reservation.no_show_at), "dd MMMM yyyy 'à' HH:mm", { locale: fr })}
                        </p>
                      )}
                      {reservation.cancel_reason && (
                        <div className="mt-2">
                          <Badge variant="outline" className="text-xs">
                            {reservation.cancel_reason === 'CLIENT' && 'Annulation client'}
                            {reservation.cancel_reason === 'OTA' && 'Annulation OTA'}
                            {reservation.cancel_reason === 'NO_SHOW' && 'No-show'}
                            {reservation.cancel_reason === 'BOOKING_ERROR' && 'Erreur de réservation'}
                            {reservation.cancel_reason === 'OTHER' && 'Autre motif'}
                          </Badge>
                        </div>
                      )}
                      {reservation.cancel_notes && (
                        <p className="text-sm mt-2 italic">{reservation.cancel_notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Payment Summary for DIRECT */}
              {canSeeFinancials && reservation.source === 'DIRECT' && (
                <div className="pt-4 border-t">
                  <div className="flex items-center gap-2 mb-3">
                    <Banknote className="w-4 h-4 text-amber-600" />
                    <p className="font-medium text-amber-800">Paiements</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4 p-3 bg-amber-50 rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Total encaissé</p>
                      <p className="font-semibold text-green-700">
                        {formatCurrency(paymentSummary.total_paid, reservation.currency)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Solde restant</p>
                      <p className={`font-bold ${paymentSummary.remaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(paymentSummary.remaining, reservation.currency)}
                      </p>
                    </div>
                    <div className="flex items-end">
                      <Badge 
                        variant={paymentSummary.is_fully_paid ? 'default' : 'outline'} 
                        className={paymentSummary.is_fully_paid ? 'bg-green-500' : 'text-amber-600 border-amber-300'}
                      >
                        {paymentSummary.is_fully_paid ? 'Soldé' : 'En attente'}
                      </Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Consultez l'onglet "Paiements" pour gérer les encaissements
                  </p>
                </div>
              )}
              
              {/* Refunds Section - visible for all reservations with financials */}
              {canSeeFinancials && (
                <div className="pt-4 border-t">
                  <RefundsList reservation={reservation} onRefresh={onRefresh} />
                </div>
              )}
              
              {reservation.notes && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm">{reservation.notes}</p>
                </div>
              )}
              
              {/* Pre-check-in Link Section */}
              <div className="pt-4 border-t">
                <PrecheckinLink reservation={reservation} onUpdate={onRefresh} />
              </div>
            </TabsContent>
            
            {/* Documents Tab */}
            <TabsContent value="documents" className="mt-4">
              <DocumentsSection
                reservation={reservation}
                paxList={reservation.pax_list || []}
                onRefresh={onRefresh}
              />
            </TabsContent>
            
            {/* Payments Tab - Only for DIRECT */}
            {reservation.source === 'DIRECT' && (
              <TabsContent value="payments" className="mt-4">
                <PaymentList
                  payments={paymentData}
                  paymentMethods={paymentMethods}
                  paymentSummary={paymentSummary}
                  reservation={reservation}
                  onAddPayment={onAddPayment}
                  onDeletePayment={onDeletePayment}
                />
              </TabsContent>
            )}
            
            {/* Events Tab */}
            <TabsContent value="events" className="mt-4">
              <EventsList
                events={reservationEvents}
                eventTypeOptions={eventTypeOptions}
                onAdd={onAddEvent}
                onEdit={onEditEvent}
                onDelete={onDeleteEvent}
              />
            </TabsContent>
            
            {/* Pax Tab */}
            <TabsContent value="pax" className="mt-4">
              <PaxManager 
                reservationId={reservation.id} 
                onUpdate={onRefresh}
              />
            </TabsContent>
          </Tabs>
        </div>
        
        <DialogFooter className="flex-wrap gap-2 flex-shrink-0 border-t pt-4 mt-4">
          <div className="flex-1 flex gap-2">
            {canCancel && (
              <Button 
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
                onClick={() => setShowCancelDialog(true)}
                data-testid="cancel-reservation-btn"
              >
                <Ban className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            )}
            {canMarkNoShow && (
              <Button 
                variant="outline"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                onClick={() => setShowNoShowDialog(true)}
                data-testid="noshow-reservation-btn"
              >
                <UserX className="w-4 h-4 mr-2" />
                No-Show
              </Button>
            )}
          </div>
          
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          {reservation?.status === 'CHECKED_IN' && (
            <Button 
              variant="outline"
              className="border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={onCheckout}
              data-testid="detail-checkout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Check-out
            </Button>
          )}
          {!isCancelled && !isNoShow && (
            <Button onClick={onEdit}>
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      
      {/* Cancel Dialog */}
      <CancelReservationDialog
        reservation={reservation}
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onCancelled={(updated) => {
          onRefresh?.();
        }}
      />
      
      {/* No-Show Dialog */}
      <NoShowDialog
        reservation={reservation}
        open={showNoShowDialog}
        onOpenChange={setShowNoShowDialog}
        onMarked={(updated) => {
          onRefresh?.();
        }}
      />
    </Dialog>
  );
}
